import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  Zoom,
  Collapse,
  Stack,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CloudUpload,
  CheckCircle,
  Person,
  Description,
  Lock,
  Home,
  Info,
  Phone,
  Email,
  CalendarToday,
  LocationOn,
  Badge,
  FamilyRestroom,
  HealthAndSafety,
  AttachFile,
  ArrowForward,
  ArrowBack,
  PictureAsPdf,
  Image as ImageIcon,
  PrintOutlined,
  MedicalServices,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import query from 'india-pincode-search';
import { athleteAPI } from '../api/athlete.api';
import { useAuth } from '../context/AuthContext';

// Constants
const REGISTRATION_TYPES = [
  { value: 'senior', label: 'Senior (15+ years)', description: 'For athletes aged 15 years and above' },
  { value: 'junior', label: 'Junior (15-20 years)', description: 'For athletes aged 15-20 years' },
  { value: 'youth', label: 'Youth (13-17 years)', description: 'For young athletes aged 13-17 years' }
];

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

const IDENTITY_PROOF_OPTIONS = [
  "Aadhar Card",
  "Passport",
  "Voter Id with complete date of birth",
  "Driving License",
  "Certificate of Identity signed by Gazetted officer",
  "Photo ID card submitted by Central Government, State Government or Public Sector Undertaking",
  "Central Government Health Scheme card or Ex-servicemen Contributory Health Scheme card"
];

const PROOF_OF_AGE_OPTIONS = [
  "Aadhar Card",
  "Passport",
  "Voter Id with complete date of birth",
  "Driving License",
  "Birth Certificate Issued by Municipal Authority or any any other authority allowed to issue birth certificates",
  "Matriculation Certificate or marksheet with DOB of recognized board",
  "Domicile certificate issued by the government",
  "Central Government Health Scheme card or Ex-servicemen Contributory Health Scheme card",
  "Marriage certificate issued by Registrar of Marriages",
  "Affidavit sworn before Magistrate mentioning date of birth"
];

const ADDRESS_PROOF_OPTIONS = [
  "Aadhar Card",
  "Passport",
  "Voter Id with complete date of birth",
  "Driving License",
  "Bank Statement not more than 3 months old with residential address",
  "Gas bill not more than 3 months old",
  "Water bill not more than 3 months old",
  "Allotment letter of accommodation issued by central or state government (not more than 3 years old)",
  "Certificate of address in original signed by Gazetted officer",
  "Domicile certificate issued by the government",
  "Landline connection bill not more than 3 months old"
];

const DOCUMENTS_WITH_BACK_PHOTO = [
  "Aadhar Card",
  "Passport",
  "Voter Id with complete date of birth",
  "Driving License"
];

const requiresBackPhoto = (documentType) => {
  return DOCUMENTS_WITH_BACK_PHOTO.includes(documentType);
};

const FILE_VALIDATIONS = {
  medicalCertificate: {
    accept: '.pdf',
    maxSize: 2 * 1024 * 1024,
    maxSizeLabel: '2MB',
    allowedTypes: ['application/pdf'],
    typeLabel: 'PDF'
  },
  photo: {
    accept: 'image/png,image/jpeg,image/jpg',
    maxSize: 500 * 1024,
    maxSizeLabel: '500KB',
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg'],
    typeLabel: 'PNG, JPG, JPEG'
  },
  document: {
    accept: 'image/png,image/jpeg,image/jpg',
    maxSize: 500 * 1024,
    maxSizeLabel: '500KB',
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg'],
    typeLabel: 'PNG, JPG, JPEG'
  }
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  paddingBottom: theme.spacing(6),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  backgroundColor: '#ffffff',
  animation: `${fadeIn} 0.6s ease-out`,
  border: '1px solid #e0e0e0',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#291e6c',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#291e6c',
    },
  },
}));

const RegistrationTypeCard = styled(Card)(({ selected }) => ({
  position: 'relative',
  cursor: 'pointer',
  border: selected ? '3px solid #291e6c' : '2px solid #e0e0e0',
  backgroundColor: selected ? '#f8f7fc' : '#fff',
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    borderColor: '#291e6c',
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(41, 30, 108, 0.15)',
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  '& fieldset': {
    borderColor: '#e0e0e0',
  },
  '&:hover fieldset': {
    borderColor: '#291e6c',
  },
  '&.Mui-focused fieldset': {
    borderColor: '#291e6c',
  },
}));

const UploadBox = styled(Box)(({ theme, hasFile, error }) => ({
  border: `2px dashed ${error ? '#a70000' : hasFile ? '#4caf50' : '#e0e0e0'}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: hasFile ? 'rgba(76, 175, 80, 0.05)' : '#fafafa',
  '&:hover': {
    borderColor: '#291e6c',
    backgroundColor: 'rgba(41, 30, 108, 0.02)',
    transform: 'translateY(-2px)',
  },
}));

const FilePreviewCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(76, 175, 80, 0.08)',
  borderRadius: theme.spacing(1),
  border: `1px solid #4caf50`,
  animation: `${fadeIn} 0.4s ease-out`,
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1100,
  backgroundColor: 'white',
  padding: theme.spacing(2, 0),
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid #e0e0e0',
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  '& .MuiStepConnector-line': {
    borderColor: '#e0e0e0',
    borderTopWidth: 3,
  },
  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
    borderColor: '#291e6c',
  },
  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
    borderColor: '#4caf50',
  },
  '& .MuiStepLabel-root .MuiStepIcon-root': {
    color: '#e0e0e0',
    fontSize: '2rem',
  },
  '& .MuiStepLabel-root .MuiStepIcon-root.Mui-active': {
    color: '#291e6c',
  },
  '& .MuiStepLabel-root .MuiStepIcon-root.Mui-completed': {
    color: '#4caf50',
  },
}));

const AdmitCardNotice = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  color: '#1976d2',
  fontSize: '0.75rem',
  fontStyle: 'italic',
}));

const steps = ['Personal Information', 'Address & Documents', 'Account Setup'];

const AthleteRegistrationForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [filePreviewUrls, setFilePreviewUrls] = useState({});
  const [emailOtp, setEmailOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [ageValidationError, setAgeValidationError] = useState('');

  const [formData, setFormData] = useState({
    registrationType: 'senior',
    firstName: '',
    middleName: '',
    surname: '',
    fatherFirstName: '',
    medicalAge: '', // NEW: Add this
    fatherMiddleName: '',
    fatherSurname: '',
    motherFirstName: '',
    motherMiddleName: '',
    motherSurname: '',
    phoneNumber: '',
    gender: '',
    aadharCardNumber: '',
    bloodGroup: '',
    kheloIndiaId: '',
    birthDate: '',
    district: '',
    stateDepartment: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    city: '',
    state: '',
    country: 'India',
    email: '',
    password: '',
    confirmPassword: '',
    identityProofType: '',
    proofOfAgeType: '',
    addressProofType: '',
  });

  const [files, setFiles] = useState({
    photo: null,
    medicalCertificate: null,
    identityProofFront: null,
    identityProofBack: null,
    proofOfAgeFront: null,
    proofOfAgeBack: null,
    addressProofFront: null,
    addressProofBack: null,
  });

  // Age validation function based on year of birth only
  const validateAgeForRegistrationType = (birthDate, registrationType) => {
    if (!birthDate || !registrationType) return null;

    const birthYear = new Date(birthDate).getFullYear();
    const currentYear = new Date().getFullYear();
    const ageByYear = currentYear - birthYear;

    switch (registrationType) {
      case 'youth':
        if (ageByYear < 13 || ageByYear > 17) {
          return 'Youth registration requires age between 13-17 years (based on year of birth). Please select the correct registration type.';
        }
        break;
      case 'junior':
        if (ageByYear < 15 || ageByYear > 20) {
          return 'Junior registration requires age between 15-20 years (based on year of birth). Please select the correct registration type.';
        }
        break;
      case 'senior':
        if (ageByYear < 15) {
          return 'Senior registration requires age 15 years or above (based on year of birth). Please select the correct registration type.';
        }
        break;
      default:
        return null;
    }
    return null;
  };

  // Check age validation whenever birthDate or registrationType changes
  useEffect(() => {
    if (formData.birthDate && formData.registrationType) {
      const error = validateAgeForRegistrationType(formData.birthDate, formData.registrationType);
      setAgeValidationError(error || '');
    } else {
      setAgeValidationError('');
    }
  }, [formData.birthDate, formData.registrationType]);

  // Helper function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function to get image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('https://') || path.startsWith('http://')) return path;
    const baseUrl = 'https://register.iwlf.in/api';
    return baseUrl.replace('/api', path);
  };

  // Load athlete data when in edit mode
  useEffect(() => {
    if (location.state?.editMode && location.state?.athleteData) {
      const athleteData = location.state.athleteData;
      setIsEditMode(true);

      // Populate form data
      setFormData({
        registrationType: athleteData.registrationType || '',
        firstName: athleteData.firstName || '',
        middleName: athleteData.middleName || '',
        surname: athleteData.surname || '',
        fatherFirstName: athleteData.fatherFirstName || '',
        fatherMiddleName: athleteData.fatherMiddleName || '',
        fatherSurname: athleteData.fatherSurname || '',
        motherFirstName: athleteData.motherFirstName || '',
        motherMiddleName: athleteData.motherMiddleName || '',
        motherSurname: athleteData.motherSurname || '',
        phoneNumber: athleteData.phoneNumber || '',
        gender: athleteData.gender || '',
        aadharCardNumber: athleteData.aadharCardNumber || '',
        bloodGroup: athleteData.bloodGroup || '',
        kheloIndiaId: athleteData.kheloIndiaId || '',
        birthDate: formatDateForInput(athleteData.birthDate) || '',
        district: athleteData.district || '',
        stateDepartment: athleteData.stateDepartment || '',
        addressLine1: athleteData.addressLine1 || '',
        addressLine2: athleteData.addressLine2 || '',
        pincode: athleteData.pincode || '',
        city: athleteData.city || '',
        state: athleteData.state || '',
        country: athleteData.country || 'India',
        email: athleteData.email || '',
        password: '',
        confirmPassword: '',
        identityProofType: athleteData.identityProof?.type || '',
        proofOfAgeType: athleteData.proofOfAge?.type || '',
        addressProofType: athleteData.addressProof?.type || '',
      });

      // Set file preview URLs for existing images
      const previews = {};
      if (athleteData.photo?.path) {
        previews.photo = getImageUrl(athleteData.photo.path);
      }
      if (athleteData.identityProof?.front?.path) {
        previews.identityProofFront = getImageUrl(athleteData.identityProof.front.path);
      }
      if (athleteData.identityProof?.back?.path) {
        previews.identityProofBack = getImageUrl(athleteData.identityProof.back.path);
      }
      if (athleteData.proofOfAge?.front?.path) {
        previews.proofOfAgeFront = getImageUrl(athleteData.proofOfAge.front.path);
      }
      if (athleteData.proofOfAge?.back?.path) {
        previews.proofOfAgeBack = getImageUrl(athleteData.proofOfAge.back.path);
      }
      if (athleteData.addressProof?.front?.path) {
        previews.addressProofFront = getImageUrl(athleteData.addressProof.front.path);
      }
      if (athleteData.addressProof?.back?.path) {
        previews.addressProofBack = getImageUrl(athleteData.addressProof.back.path);
      }
      if (athleteData.medicalCertificate?.filename) {
        previews.medicalCertificate = athleteData.medicalCertificate.filename;
      }

      setFilePreviewUrls(previews);
      toast.info('Editing athlete profile. Update the fields you want to change.');
    }
  }, [location.state]);

  const calculateProgress = () => {
    return (activeStep / steps.length) * 100;
  };

  const validateFile = (file, validationType) => {
    const validation = FILE_VALIDATIONS[validationType];

    if (file.size > validation.maxSize) {
      return `File size must be less than ${validation.maxSizeLabel}`;
    }
    console.log(file.type);
    
    if (!validation.allowedTypes.includes(file.type)) {
      return `Only ${validation.typeLabel} files are allowed`;
    }

    return null;
  };

  const sendOtp = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email to send OTP.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    try {
      await athleteAPI.sendVerificationOTP(formData.email);
      toast.success('OTP sent to your email.');
      setOtpSent(true);
      setOtpVerified(false);
    } catch (error) {
      setOtpError(error.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!emailOtp) {
      setOtpError('Please enter the OTP.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    try {
      await athleteAPI.verifyRegistrationOTP({ email: formData.email, otp: emailOtp });
      toast.success('Email verified successfully.');
      setOtpVerified(true);
    } catch (error) {
      setOtpError(error.response?.data?.message || 'Invalid OTP.');
      setOtpVerified(false);
    } finally {
      setOtpLoading(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.registrationType) {
        newErrors.registrationType = 'Please select a registration type';
      }

      if (['junior', 'youth'].includes(formData.registrationType)) {
    if (!formData.medicalAge) {
      newErrors.medicalAge = 'Medical age is required for Junior and Youth categories';
    } else if (formData.medicalAge < 0 || formData.medicalAge > 150) {
      newErrors.medicalAge = 'Medical age must be between 0 and 150';
    } else if (!Number.isInteger(Number(formData.medicalAge))) {
      newErrors.medicalAge = 'Medical age must be a whole number';
    }
  }

      if (!formData.firstName || formData.firstName.length < 2) {
        newErrors.firstName = 'First name must be at least 2 characters';
      }

      if (!formData.surname || formData.surname.length < 2) {
        newErrors.surname = 'Surname must be at least 2 characters';
      }

      if (!formData.fatherFirstName) {
        newErrors.fatherFirstName = 'Father first name is required';
      }

      if (!formData.fatherSurname) {
        newErrors.fatherSurname = 'Father surname is required';
      }

      if (!formData.phoneNumber || !/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Phone number must be 10 digits starting with 6-9';
      }

      if (!formData.gender) {
        newErrors.gender = 'Gender is required';
      }

      if (!formData.aadharCardNumber || !/^[2-9]\d{11}$/.test(formData.aadharCardNumber)) {
        newErrors.aadharCardNumber = 'Aadhar must be 12 digits and cannot start with 0 or 1';
      }

      // Khelo India ID validation - exactly 10 characters if provided
      if (formData.kheloIndiaId && formData.kheloIndiaId.length !== 10) {
        newErrors.kheloIndiaId = 'Khelo India ID must be exactly 10 characters';
      }

      if (!formData.birthDate) {
        newErrors.birthDate = 'Birth date is required';
      } else {
        const birthDate = new Date(formData.birthDate);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();

        if (age < 5 || age > 100) {
          newErrors.birthDate = 'Age must be between 5 and 100 years';
        }

        // Check age validation for registration type
        const ageError = validateAgeForRegistrationType(formData.birthDate, formData.registrationType);
        if (ageError) {
          newErrors.birthDate = ageError;
        }
      }

      // In edit mode, photo is optional if already exists
      if (!files.photo && !filePreviewUrls.photo && !isEditMode) {
        newErrors.photo = 'Photo is required';
      }

      // Medical certificate required for youth/junior
      if ((formData.registrationType === 'youth' || formData.registrationType === 'junior') &&
        !files.medicalCertificate && !filePreviewUrls.medicalCertificate) {
        newErrors.medicalCertificate = 'Medical certificate is required';
      }
    }

    if (step === 1) {
      if (!formData.addressLine1 || formData.addressLine1.length < 5) {
        newErrors.addressLine1 = 'Address must be at least 5 characters';
      }

      if (!formData.pincode || !/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'Pincode must be exactly 6 digits';
      }

      if (!formData.city) {
        newErrors.city = 'City is required';
      }

      if (!formData.state) {
        newErrors.state = 'State is required';
      }

      if (!formData.identityProofType) {
        newErrors.identityProofType = 'Identity proof type is required';
      }

      if (!formData.proofOfAgeType) {
        newErrors.proofOfAgeType = 'Proof of age type is required';
      }

      if (!formData.addressProofType) {
        newErrors.addressProofType = 'Address proof type is required';
      }

      // In edit mode, documents are optional if already exist
      if (!files.identityProofFront && !filePreviewUrls.identityProofFront) {
        newErrors.identityProofFront = 'Identity proof front is required';
      }

      if (requiresBackPhoto(formData.identityProofType) && !files.identityProofBack && !filePreviewUrls.identityProofBack) {
        newErrors.identityProofBack = 'Identity proof back is required';
      }

      if (!files.proofOfAgeFront && !filePreviewUrls.proofOfAgeFront) {
        newErrors.proofOfAgeFront = 'Proof of age front is required';
      }

      if (requiresBackPhoto(formData.proofOfAgeType) && !files.proofOfAgeBack && !filePreviewUrls.proofOfAgeBack) {
        newErrors.proofOfAgeBack = 'Proof of age back is required';
      }

      if (!files.addressProofFront && !filePreviewUrls.addressProofFront) {
        newErrors.addressProofFront = 'Address proof front is required';
      }

      if (requiresBackPhoto(formData.addressProofType) && !files.addressProofBack && !filePreviewUrls.addressProofBack) {
        newErrors.addressProofBack = 'Address proof back is required';
      }
    }

    if (step === 2) {
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Valid email is required';
      }

      // Password validation
      if (!isEditMode) {
        // New registration - password is mandatory
        if (!otpVerified) {
          newErrors.otp = 'Please verify your email with OTP before submitting';
        }

        if (!formData.password) {
          newErrors.password = 'Password is required'
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters'
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password)) {
          newErrors.password = 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 symbol'
        }

        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!termsAccepted) {
          newErrors.terms = 'You must accept the terms and conditions';
        }
      } else {
        // Edit mode - password is optional but must be valid if provided
        if (formData.password) {
          if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password)) {
      newErrors.password = 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 symbol'
    }

          if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear back photo file when document type changes to one that doesn't require it
    if (name === 'identityProofType' && !requiresBackPhoto(value)) {
      setFiles((prev) => ({ ...prev, identityProofBack: null }));
      setFilePreviewUrls((prev) => ({ ...prev, identityProofBack: null }));
      setErrors((prev) => ({ ...prev, identityProofBack: '' }));
    }

    if (name === 'proofOfAgeType' && !requiresBackPhoto(value)) {
      setFiles((prev) => ({ ...prev, proofOfAgeBack: null }));
      setFilePreviewUrls((prev) => ({ ...prev, proofOfAgeBack: null }));
      setErrors((prev) => ({ ...prev, proofOfAgeBack: '' }));
    }

    if (name === 'addressProofType' && !requiresBackPhoto(value)) {
      setFiles((prev) => ({ ...prev, addressProofBack: null }));
      setFilePreviewUrls((prev) => ({ ...prev, addressProofBack: null }));
      setErrors((prev) => ({ ...prev, addressProofBack: '' }));
    }

    // Clear error if exists
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Helper function to convert uppercase strings to title case
  const toTitleCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handlePincodeChange = (e) => {
    const { value } = e.target;

    // Update pincode value
    setFormData((prev) => ({ ...prev, pincode: value }));

    // Clear error if exists
    if (errors.pincode) {
      setErrors((prev) => ({ ...prev, pincode: '' }));
    }

    // Auto-fetch city and state when pincode is 6 digits
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      try {
        const results = query.search(value);

        if (results && results.length > 0) {
          const locationData = results[0];

          // Convert to title case to match dropdown values
          const normalizedState = toTitleCase(locationData.state || '');
          const normalizedCity = toTitleCase(locationData.city || locationData.district || '');
          const normalizedDistrict = toTitleCase(locationData.district || '');

          // Auto-fill city and state with normalized values
          setFormData((prev) => ({
            ...prev,
            city: normalizedCity,
            state: normalizedState,
            district: normalizedDistrict
          }));

          // Clear any previous errors
          setErrors((prev) => ({
            ...prev,
            city: '',
            state: '',
            pincode: ''
          }));

          toast.success('Location details auto-filled from pincode');
        } else {
          toast.warning('No location found for this pincode');
        }
      } catch (error) {
        console.error('Error fetching pincode data:', error);
        toast.error('Failed to fetch location details');
      }
    }
  };

  const handleFileChange = (e, fieldName, validationType) => {
    const file = e.target.files[0];

    if (file) {
      const validationError = validateFile(file, validationType);

      if (validationError) {
        setErrors((prev) => ({ ...prev, [fieldName]: validationError }));
        e.target.value = '';
        return;
      }

      setFiles((prev) => ({ ...prev, [fieldName]: file }));

      // Create preview URL for image files
      if (validationType === 'photo' || validationType === 'document') {
        const previewUrl = URL.createObjectURL(file);
        setFilePreviewUrls((prev) => ({ ...prev, [fieldName]: previewUrl }));
      }

      if (errors[fieldName]) {
        setErrors((prev) => ({ ...prev, [fieldName]: '' }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setLoading(true);

    try {
      const submitData = new FormData();

      // Append all form data except confirmPassword
      Object.keys(formData).forEach(key => {
        if (formData[key] && key !== 'confirmPassword' && key !== 'password') {
          submitData.append(key, formData[key]);
        }
      });

      // Only append password if not in edit mode
      if (!isEditMode && formData.password) {
        submitData.append('password', formData.password);
      }

      // Append all files (only new uploads)
      Object.keys(files).forEach(key => {
        if (files[key]) {
          submitData.append(key, files[key]);
        }
      });
      
      let response;
      if (isEditMode) {
        // Update profile
        response = await athleteAPI.updateProfile(submitData);
        toast.success('Profile updated successfully!');
        setTimeout(() => {
          navigate('/payment');
        }, 1500);
      } else {
        // New registration
        response = await athleteAPI.register(submitData);

        localStorage.setItem('athleteId', response.data.athleteId);

        login(
          response.data,
          response.data.token,
          'athlete'
        );

        if (response.resumePayment) {
          toast.info(response.message || 'You have a pending registration. Redirecting to payment...');
        } else {
          toast.success('Registration successful! Please proceed to payment.');
        }

        setTimeout(() => {
          navigate('/payment');
        }, 1500);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        'Operation failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderFileUpload = (id, label, fieldName, validationType, required = false) => {
    const validation = FILE_VALIDATIONS[validationType];
    const isPdf = validationType === 'medicalCertificate';
    const hasExistingFile = filePreviewUrls[fieldName];

    return (
      <Box>
        <input
          accept={validation.accept}
          style={{ display: 'none' }}
          id={id}
          type="file"
          onChange={(e) => handleFileChange(e, fieldName, validationType)}
        />
        <label htmlFor={id}>
          <UploadBox
            hasFile={files[fieldName] || hasExistingFile}
            error={errors[fieldName]}
            component="div"
          >
            {isPdf ? (
              <PictureAsPdf sx={{ fontSize: 48, color: '#a70000', mb: 2 }} />
            ) : (
              <CloudUpload sx={{ fontSize: 48, color: '#291e6c', mb: 2 }} />
            )}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {label} {required && '*'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Click to browse or drag and drop
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {validation.typeLabel} (Max {validation.maxSizeLabel})
            </Typography>
          </UploadBox>
        </label>

        {errors[fieldName] && (
          <Alert severity="error" sx={{ mt: 1, borderRadius: 1 }}>
            {errors[fieldName]}
          </Alert>
        )}

        {/* Show existing file preview */}
        {hasExistingFile && !files[fieldName] && (
          <FilePreviewCard>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              <CheckCircle sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              Current File
            </Typography>
            {!isPdf && (
              <Box
                component="img"
                src={hasExistingFile}
                alt="Preview"
                sx={{ width: '100%', maxWidth: 200, mt: 1, borderRadius: 1 }}
              />
            )}
            {isPdf && (
              <Typography variant="body2">{hasExistingFile}</Typography>
            )}
          </FilePreviewCard>
        )}

        {/* Show new file upload */}
        {files[fieldName] && (
          <FilePreviewCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" color="success.main">
                  {files[fieldName].name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(files[fieldName].size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
              {filePreviewUrls[fieldName] && !isPdf && (
                <Box
                  component="img"
                  src={filePreviewUrls[fieldName]}
                  alt="Preview"
                  sx={{ width: 60, height: 60, borderRadius: 1, objectFit: 'cover' }}
                />
              )}
              {isPdf ? <PictureAsPdf color="error" /> : <CheckCircle color="success" />}
            </Stack>
          </FilePreviewCard>
        )}
      </Box>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            {/* Registration Type Selection */}
            <SectionHeader>
              <Person sx={{ fontSize: 28, color: '#291e6c' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="#291e6c">
                  Personal Information
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Please provide accurate personal details
                </Typography>
              </Box>
            </SectionHeader>

            {/* Show edit mode banner */}
            {isEditMode && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 1.5 }}>
                You are editing your profile. Update any fields you want to change.
              </Alert>
            )}

            {/* Registration Type */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Select Registration Category *
              </Typography>
              <Grid container spacing={2}>
                {REGISTRATION_TYPES.map((type) => (
                  <Grid item xs={12} md={4} key={type.value}>
                    <RegistrationTypeCard
                      selected={formData.registrationType === type.value}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, registrationType: type.value }));
                        setErrors((prev) => ({ ...prev, registrationType: '' }));
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {type.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {type.description}
                        </Typography>
                        {formData.registrationType === type.value && (
                          <CheckCircle
                            sx={{
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              color: '#291e6c',
                              fontSize: 28
                            }}
                          />
                        )}
                      </CardContent>
                    </RegistrationTypeCard>
                  </Grid>
                ))}
              </Grid>

              {errors.registrationType && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
                  {errors.registrationType}
                </Alert>
              )}
            </Box>

            <Alert
              severity="info"
              icon={<Info />}
              sx={{ borderRadius: 1.5, mt: 2 }}
            >
              <Typography variant="body2">
                {(formData.registrationType === 'youth' || formData.registrationType === 'junior')
                  ? '• Medical certificate is mandatory (PDF format, max 2MB)'
                  : '• No medical certificate required'}
              </Typography>
            </Alert>

            {/* Athlete Details */}
            <SectionHeader sx={{ mt: 4 }}>
              <Badge sx={{ fontSize: 28, color: '#291e6c' }} />
              <Typography variant="h6" fontWeight={700} color="#291e6c">
                Athlete Details
              </Typography>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  required
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>

              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  label="Middle Name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>

              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  required
                  label="Surname"
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  error={!!errors.surname}
                  helperText={errors.surname}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>
            </Grid>

            {/* Father's Information */}
            <SectionHeader sx={{ mt: 4 }}>
              <FamilyRestroom sx={{ fontSize: 28, color: '#291e6c' }} />
              <Typography variant="h6" fontWeight={700} color="#291e6c">
                Father's Information
              </Typography>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  required
                  label="Father's First Name"
                  name="fatherFirstName"
                  value={formData.fatherFirstName}
                  onChange={handleChange}
                  error={!!errors.fatherFirstName}
                  helperText={errors.fatherFirstName}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>

              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  label="Father's Middle Name"
                  name="fatherMiddleName"
                  value={formData.fatherMiddleName}
                  onChange={handleChange}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>

              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  required
                  label="Father's Surname"
                  name="fatherSurname"
                  value={formData.fatherSurname}
                  onChange={handleChange}
                  error={!!errors.fatherSurname}
                  helperText={errors.fatherSurname}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>
            </Grid>

            {/* Mother's Information */}
            <SectionHeader sx={{ mt: 4 }}>
              <FamilyRestroom sx={{ fontSize: 28, color: '#291e6c' }} />
              <Typography variant="h6" fontWeight={700} color="#291e6c">
                Mother's Information
              </Typography>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  label="Mother's First Name"
                  name="motherFirstName"
                  value={formData.motherFirstName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  label="Mother's Middle Name"
                  name="motherMiddleName"
                  value={formData.motherMiddleName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  label="Mother's Surname"
                  name="motherSurname"
                  value={formData.motherSurname}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            {/* Contact & Identity */}
            <SectionHeader sx={{ mt: 4 }}>
              <Phone sx={{ fontSize: 28, color: '#291e6c' }} />
              <Typography variant="h6" fontWeight={700} color="#291e6c">
                Contact & Identity Information
              </Typography>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  required
                  label="Phone Number"
                  inputProps={{ maxLength: 10 }}
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.gender} required>
                  <InputLabel>Gender</InputLabel>
                  <StyledSelect
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                  >
                    {GENDER_OPTIONS.map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {gender}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                </FormControl>
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  required
                  label="Aadhar Card Number"
                  name="aadharCardNumber"
                  value={formData.aadharCardNumber}
                  onChange={handleChange}
                  error={!!errors.aadharCardNumber}
                  helperText={errors.aadharCardNumber}
                  inputProps={{ maxLength: 12 }}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Blood Group</InputLabel>
                  <StyledSelect
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleChange}
                    label="Blood Group"
                  >
                    {BLOOD_GROUPS.map((group) => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Khelo India ID (Optional)"
                  name="kheloIndiaId"
                  value={formData.kheloIndiaId}
                  onChange={handleChange}
                  error={!!errors.kheloIndiaId}
                  helperText={errors.kheloIndiaId || 'Must be exactly 10 characters if provided'}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  required
                  label="Date of Birth"
                  name="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleChange}
                  error={!!errors.birthDate}
                  helperText={errors.birthDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onClick={(e) => {
                    // Open the native date picker when clicking anywhere in the field
                    e.currentTarget.querySelector('input[type="date"]')?.showPicker();
                  }}
                  InputProps={{
                    onClick: (e) => {
                      // Also handle clicks on the input itself
                      e.currentTarget.showPicker?.();
                    }
                  }}
                />
                <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
              </Grid>
{/* Medical Age - Junior/Youth Only */}
{['junior', 'youth'].includes(formData.registrationType) && (
  <Grid item xs={12} sm={6}>
    <StyledTextField
      fullWidth
      required
      label="Medical Age"
      name="medicalAge"
      type="number"
      value={formData.medicalAge || ''}
      onChange={handleChange}
      error={!!errors.medicalAge}
      helperText={
        errors.medicalAge || 
        "Enter your medical age as per medical certificate (can be different from calculated age)"
      }
      InputProps={{
        inputProps: { 
          min: 0, 
          max: 150,
          step: 1
        },
        startAdornment: (
          <InputAdornment position="start">
            <MedicalServices />
          </InputAdornment>
        ),
      }}
    />
    <AdmitCardNotice>
                  <PrintOutlined sx={{ fontSize: 14 }} />
                  This field will be printed on the admit card
                </AdmitCardNotice>
  </Grid>
)}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="District"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="State Department"
                  name="stateDepartment"
                  value={formData.stateDepartment}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            {/* Age Validation Error Alert */}
            {ageValidationError && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {ageValidationError}
                </Typography>
              </Alert>
            )}

            {/* Photo Upload */}
            <SectionHeader sx={{ mt: 4 }}>
              <AttachFile sx={{ fontSize: 28, color: '#291e6c' }} />
              <Typography variant="h6" fontWeight={700} color="#291e6c">
                Upload Documents
              </Typography>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {renderFileUpload('photo-upload', 'Passport Size Photo', 'photo', 'photo', true)}
              </Grid>

              {(formData.registrationType === 'youth' || formData.registrationType === 'junior') && (
                <Grid item xs={12} md={6}>
                  {renderFileUpload('medical-certificate-upload', 'Medical Certificate', 'medicalCertificate', 'medicalCertificate', true)}
                </Grid>
              )}
            </Grid>
          </>
        );

      case 1:
        return (
          <>
            <SectionHeader>
              <Home sx={{ fontSize: 28, color: '#291e6c' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="#291e6c">
                  Address & Documents
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Provide residential address and upload required documents
                </Typography>
              </Box>
            </SectionHeader>

            {/* Residential Address */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Residential Address
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    required
                    label="Address Line 1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleChange}
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1}
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Address Line 2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    required
                    label="Pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handlePincodeChange}
                    error={!!errors.pincode}
                    helperText={errors.pincode}
                    inputProps={{ maxLength: 6 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    required
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    error={!!errors.city}
                    helperText={errors.city}
                  />
                  <AdmitCardNotice>
                    <PrintOutlined sx={{ fontSize: 14 }} />
                    This field will be printed on the admit card
                  </AdmitCardNotice>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.state} required>
                    <InputLabel>State</InputLabel>
                    <StyledSelect
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      label="State"
                    >
                      {INDIAN_STATES.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    {errors.state && <FormHelperText>{errors.state}</FormHelperText>}
                  </FormControl>
                  <AdmitCardNotice>
                    <PrintOutlined sx={{ fontSize: 14 }} />
                    This field will be printed on the admit card
                  </AdmitCardNotice>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    disabled
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Document Uploads */}
            <SectionHeader>
              <Description sx={{ fontSize: 28, color: '#291e6c' }} />
              <Typography variant="h6" fontWeight={700} color="#291e6c">
                Document Uploads
              </Typography>
            </SectionHeader>

            <Alert severity="info" icon={<Info />} sx={{ borderRadius: 1.5 }}>
              Back side photo is required only for Aadhar, Passport, Voter ID & Driving License. All documents must be PNG/JPG/JPEG (max 500KB).
            </Alert>

            {/* Identity Proof */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Identity Proof
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.identityProofType} required>
                    <InputLabel>Identity Proof Type</InputLabel>
                    <StyledSelect
                      name="identityProofType"
                      value={formData.identityProofType}
                      onChange={handleChange}
                      label="Identity Proof Type"
                    >
                      {IDENTITY_PROOF_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    {errors.identityProofType && (
                      <FormHelperText>{errors.identityProofType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {formData.identityProofType && (
                  <>
                    <Grid item xs={12} md={requiresBackPhoto(formData.identityProofType) ? 6 : 12}>
                      {renderFileUpload('identity-proof-front', 'Identity Proof - Front Side', 'identityProofFront', 'document', true)}
                    </Grid>

                    {requiresBackPhoto(formData.identityProofType) && (
                      <Grid item xs={12} md={6}>
                        {renderFileUpload('identity-proof-back', 'Identity Proof - Back Side', 'identityProofBack', 'document', true)}
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>

            {/* Proof of Age */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Proof of Age
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.proofOfAgeType} required>
                    <InputLabel>Proof of Age Type</InputLabel>
                    <StyledSelect
                      name="proofOfAgeType"
                      value={formData.proofOfAgeType}
                      onChange={handleChange}
                      label="Proof of Age Type"
                    >
                      {PROOF_OF_AGE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    {errors.proofOfAgeType && (
                      <FormHelperText>{errors.proofOfAgeType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {formData.proofOfAgeType && (
                  <>
                    <Grid item xs={12} md={requiresBackPhoto(formData.proofOfAgeType) ? 6 : 12}>
                      {renderFileUpload('proof-of-age-front', 'Proof of Age - Front Side', 'proofOfAgeFront', 'document', true)}
                    </Grid>

                    {requiresBackPhoto(formData.proofOfAgeType) && (
                      <Grid item xs={12} md={6}>
                        {renderFileUpload('proof-of-age-back', 'Proof of Age - Back Side', 'proofOfAgeBack', 'document', true)}
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>

            {/* Address Proof */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Address Proof
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.addressProofType} required>
                    <InputLabel>Address Proof Type</InputLabel>
                    <StyledSelect
                      name="addressProofType"
                      value={formData.addressProofType}
                      onChange={handleChange}
                      label="Address Proof Type"
                    >
                      {ADDRESS_PROOF_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    {errors.addressProofType && (
                      <FormHelperText>{errors.addressProofType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {formData.addressProofType && (
                  <>
                    <Grid item xs={12} md={requiresBackPhoto(formData.addressProofType) ? 6 : 12}>
                      {renderFileUpload('address-proof-front', 'Address Proof - Front Side', 'addressProofFront', 'document', true)}
                    </Grid>

                    {requiresBackPhoto(formData.addressProofType) && (
                      <Grid item xs={12} md={6}>
                        {renderFileUpload('address-proof-back', 'Address Proof - Back Side', 'addressProofBack', 'document', true)}
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          </>
        );

      case 2:
        return (
          <>
            <SectionHeader>
              <Lock sx={{ fontSize: 28, color: '#291e6c' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="#291e6c">
                  Account Setup
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isEditMode ? 'Verify your email address' : 'Create your login credentials'}
                </Typography>
              </Box>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <StyledTextField
                    fullWidth
                    required
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                      setOtpSent(false);
                      setOtpVerified(false);
                      setEmailOtp('');
                      if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                      if (errors.otp) setErrors(prev => ({ ...prev, otp: '' }));
                    }}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={isEditMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <NavigationButton
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      sendOtp();
                    }}
                    disabled={otpLoading || !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {otpLoading ? <CircularProgress size={24} /> : otpSent ? 'Resend OTP' : 'Send OTP'}
                  </NavigationButton>
                </Stack>
              </Grid>

              {otpSent && (
                <>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <StyledTextField
                        fullWidth
                        required
                        label="Enter OTP"
                        value={emailOtp}
                        onChange={(e) => {
                          setEmailOtp(e.target.value);
                          if (errors.otp) setErrors(prev => ({ ...prev, otp: '' }));
                          setOtpError('');
                        }}
                        error={!!errors.otp || !!otpError}
                        helperText={errors.otp || otpError}
                      />
                      <NavigationButton
                        variant="contained"
                        onClick={verifyOtp}
                        disabled={otpLoading || otpVerified}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {otpLoading ? <CircularProgress size={24} /> : otpVerified ? 'Verified' : 'Verify OTP'}
                      </NavigationButton>
                    </Stack>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label={isEditMode ? 'New Password (Optional)' : 'Password'}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required={!isEditMode}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, password: e.target.value }));
                    if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                  }}
                  error={!!errors.password}
                  helperText={errors.password ? errors.password : isEditMode ? 'Leave blank to keep current password. Min 8 characters if changing.' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required={!isEditMode || formData.password}
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
                    if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {!isEditMode ? (
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => {
                        setTermsAccepted(e.target.checked);
                        if (errors.terms) {
                          setErrors((prev) => ({ ...prev, terms: '' }));
                        }
                      }}
                      sx={{
                        color: '#291e6c',
                        '&.Mui-checked': {
                          color: '#291e6c',
                        },
                      }}
                      size="large"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I accept the terms and conditions and agree to provide accurate information *
                    </Typography>
                  }
                />
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 3, borderRadius: 1.5 }}>
                By updating your profile, you confirm that all information provided is accurate.
              </Alert>
            )}

            {errors.terms && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
                {errors.terms}
              </Alert>
            )}

            {errors.submit && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
                {errors.submit}
              </Alert>
            )}

            <Alert severity="success" icon={<CheckCircle />} sx={{ borderRadius: 1.5, mt: 3 }}>
              {isEditMode ? 'Review your changes and click Update to save.' : 'Almost Done! Click Submit to complete your registration.'}
            </Alert>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        <StyledPaper elevation={0}>
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: "'Oswald', sans-serif" }}>
              {isEditMode ? 'EDIT ATHLETE PROFILE' : 'ATHLETE REGISTRATION'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isEditMode ? 'Update your profile information' : 'Complete the form below to register as an athlete'}
            </Typography>
          </Box>

          <StyledStepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>

          <Divider sx={{ my: 4 }} />

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            {activeStep > 0 && (
              <NavigationButton
                onClick={handleBack}
                startIcon={<ArrowBack />}
                variant="outlined"
                sx={{
                  borderColor: '#291e6c',
                  color: '#291e6c',
                  '&:hover': {
                    borderColor: '#1f1750',
                    backgroundColor: 'rgba(41, 30, 108, 0.05)',
                  },
                }}
              >
                Previous
              </NavigationButton>
            )}

            <NavigationButton
              onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              endIcon={activeStep === steps.length - 1 ? null : <ArrowForward />}
              variant="contained"
              disabled={loading || ageValidationError !== ''}
              sx={{
                marginLeft: 'auto',
                backgroundColor: '#291e6c',
                '&:hover': {
                  backgroundColor: '#1f1750',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                isEditMode ? 'Update Profile' : 'Submit Registration'
              ) : (
                'Continue'
              )}
            </NavigationButton>
          </Box>
        </StyledPaper>
      </Container>
    </PageWrapper>
  );
};

export default AthleteRegistrationForm;
